import { Tooltip } from "@chakra-ui/react";
import React, { useState } from "react";

import { Button, ConfirmModal } from "../../../components";
import { AiInterviewerStatus } from "../../graphql";
import {
  REQUIRES_BH_SCREEN_MANAGE_PERMISSION,
  useBHScreenPermission,
} from "../../hooks/useBHScreenPermission";
import PublishWarningsModal from "./PublishWarningsModal";
import { useAiInterviewerFormContext } from "./useAiInterviewerFormContext";
import { getPublishWarnings, PublishWarning } from "./utils";

interface AiInterviewerStatusButtonProps {
  status?: AiInterviewerStatus | null;
  updateStatus: (newStatus: AiInterviewerStatus) => void;
  updateLoading: boolean;
}

export const AiInterviewerArchiveButton: React.FC<
  AiInterviewerStatusButtonProps
> = ({ status, updateStatus, updateLoading }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { canManage } = useBHScreenPermission();

  if (!status || status === AiInterviewerStatus.Archived) {
    return null;
  }

  const modalBody =
    "Archiving will remove this AI Interviewer from active use. You can unarchive it later. Are you sure?";

  return (
    <>
      <Tooltip
        label={!canManage ? REQUIRES_BH_SCREEN_MANAGE_PERMISSION : "Archive"}
        placement="bottom-end"
      >
        <Button
          size="sm"
          variant="prismSecondary"
          onClick={() => setIsModalOpen(true)}
          isLoading={updateLoading}
          isDisabled={!canManage}
        >
          Archive
        </Button>
      </Tooltip>
      <ConfirmModal
        isOpen={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onConfirm={() => {
          updateStatus(AiInterviewerStatus.Archived);
          setIsModalOpen(false);
        }}
        modalTitleText="Archive AI Interviewer?"
        modalBodyText={modalBody}
        modalSubmitText="Archive"
        isDangerous
      />
    </>
  );
};

interface AiInterviewerUnarchiveButtonProps {
  status?: AiInterviewerStatus | null;
  onUnarchive: () => void;
  loading: boolean;
}

export const AiInterviewerUnarchiveButton: React.FC<
  AiInterviewerUnarchiveButtonProps
> = ({ status, onUnarchive, loading }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { canManage } = useBHScreenPermission();

  if (status !== AiInterviewerStatus.Archived) {
    return null;
  }

  return (
    <>
      <Tooltip
        label={!canManage ? REQUIRES_BH_SCREEN_MANAGE_PERMISSION : "Unarchive"}
        placement="bottom-end"
      >
        <Button
          size="sm"
          variant="prismSecondary"
          onClick={() => setIsModalOpen(true)}
          isLoading={loading}
          isDisabled={!canManage}
        >
          Unarchive
        </Button>
      </Tooltip>
      <ConfirmModal
        isOpen={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onConfirm={() => {
          onUnarchive();
          setIsModalOpen(false);
        }}
        modalTitleText="Unarchive AI Interviewer?"
        modalBodyText="This will restore the AI Interviewer as a draft. Are you sure?"
        modalSubmitText="Unarchive"
        isDangerous={false}
      />
    </>
  );
};

export const AiInterviewerPublishButton: React.FC<
  AiInterviewerStatusButtonProps
> = ({ status, updateStatus, updateLoading }) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isWarningsOpen, setIsWarningsOpen] = useState(false);
  const [warnings, setWarnings] = useState<PublishWarning[]>([]);
  const { getValues } = useAiInterviewerFormContext();

  if (status !== AiInterviewerStatus.Draft) {
    return null;
  }

  const handlePublishClick = (): void => {
    const formValues = getValues();
    const detected = getPublishWarnings(formValues);
    if (detected.length > 0) {
      setWarnings(detected);
      setIsWarningsOpen(true);
    } else {
      setIsConfirmOpen(true);
    }
  };

  const doPublish = (): void => {
    updateStatus(AiInterviewerStatus.Live);
    setIsConfirmOpen(false);
    setIsWarningsOpen(false);
  };

  return (
    <>
      <Button
        size="sm"
        variant="prismSecondary"
        onClick={handlePublishClick}
        isLoading={updateLoading}
      >
        Publish
      </Button>
      <ConfirmModal
        isOpen={isConfirmOpen}
        onCancel={() => setIsConfirmOpen(false)}
        onConfirm={doPublish}
        modalTitleText="Publish AI Interviewer?"
        modalBodyText="Publishing will make this AI Interviewer live and available for use. Are you sure?"
        modalSubmitText="Publish"
        isDangerous={false}
      />
      <PublishWarningsModal
        isOpen={isWarningsOpen}
        warnings={warnings}
        onClose={() => setIsWarningsOpen(false)}
        onPublishAnyway={doPublish}
      />
    </>
  );
};
